import { useState, type FC } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import {
  UiText,
  uiStyles,
  UiHStack,
  UiIconCaretRight,
  UiIconCreditCard
} from '@/lib/ui';
import PayByInvoiceDrawer from './PayByInvoiceDrawer';
import { type ApiResponse } from '@/api/tenantClient';
import { cartQueryKey, listOrdersQueryKey, type PaymentIntent, preparePayment, type PreparePaymentRequest, registrationQueryKey } from '@/api/registration';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';

export interface PayByInvoiceProps {
  isUpsell?: boolean
}

const PayByInvoice: FC<PayByInvoiceProps> = ({ isUpsell }) => {
  const { createTenantApiRequest } = useTenantApi();
  const { isOpen, onToggle } = useDisclosure();
  const [errors, setErrors] = useState<string[]>([]);
  const { eventId } = useRegisterRoute();
  const { data: cart } = useCartQuery(eventId);
  const queryClient = useQueryClient();
  const { userSession } = useUserSession();
  const registrationQuery = useRegistrationQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading: isPreparePaymentLoading } = useMutation<ApiResponse<PaymentIntent>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<PaymentIntent>;
    },
    onSuccess: (order: ApiResponse<PaymentIntent>) => {
      if (order.errors && order.errors.length > 0) {
        setErrors(order.errors);
      } else {
        void queryClient.invalidateQueries({ queryKey: [registrationQuery] });
        void queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
        void queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
      };
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  const CustomToggle = () => {
    if (!cart) {
      return;
    }
    const preparePaymentData: PreparePaymentRequest = {
      cartId: cart.id,
      locale: 'aud',
      gateway: 'invoice',
      withoutSendMail: true
    };
    preparePaymentMutate(preparePaymentData);
    onToggle();
  };

  return (
    <>
      <UiHStack
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        {...uiStyles.hover}
        onClick={CustomToggle}
        justifyContent={'space-between'}
        px={{ base: 4, lg: 8 }}
        py={4}
        spacing={4}
        shadow={uiStyles.cardShadow}
      >
        <UiIconCreditCard size={'3xl'} color={'indigo.400'} />
        <UiText variant={'body1'} flexGrow={1}>Pay by invoice (offline)</UiText>
        <UiIconCaretRight size={'3xl'} color={'primary.500'} />
      </UiHStack>
      <PayByInvoiceDrawer
        isOpen={isOpen}
        onClose={onToggle}
        isUpsell={isUpsell}
      />
    </>
  );
};

export default PayByInvoice;
